import { computed } from 'vue';
import { useAvailability } from '@nuxt-common/media-card-large/app/composables/useAvailability';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useTopLevelCategoryFlags } from '~/modules/featureControl/composables/useTopLevelCategoryFlags';
import { useType } from '~/modules/type/composables/useType';
import { useProduct } from '~/modules/product/composables/useProduct';
import useSize from '~/modules/size/composables/useSize';
import useUpc from '~/modules/upc/composables/useUpc';
import { isAnyUpcAvailable } from '../utils/utils';

function typeDrivenMapper(availability, types) {
  return types?.map((type) => ({ ...type, available: isAnyUpcAvailable(availability, { typeId: type.id }) })) || [];
}

export default function useTypeVariant() {
  const { isTypeDrivenThirdVariantFlag: isTypeDriven } = useProductFlags();
  const { isBeddingCategory } = useTopLevelCategoryFlags();
  const { productId, productData: product } = useProduct();
  const { selectedSizeId } = useSize();
  const { type: typeAvailability } = useAvailability(productId);
  const { traitsMap } = useUpc();
  const { types } = useType();

  /**
   * @type {import('vue').ComputedRef<Type[]>}
   */
  const filteredTypes = computed(() => {
    if (isTypeDriven) {
      return typeDrivenMapper(traitsMap.value, types.value);
    }

    if (isBeddingCategory) {
      return types.value;
    }

    const size = product.traits.sizes.sizeMap[selectedSizeId.value ?? -1] || { typeToColors: {} };
    const typeIdsForSize = Object.keys(size.typeToColors).map((id) => parseInt(id, 10)) || [];

    return types.value.filter((type) => typeIdsForSize.includes(type.id));
  });

  const availableTypes = computed(() => filteredTypes.value.filter((type) => typeAvailability.value[type.id]));

  return {
    filteredTypes,
    availableTypes,
  };
}
